import React, { useState, useEffect } from 'react';
import { Typography, Box, CircularProgress, TextField, IconButton, MenuItem } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { useNavigate } from 'react-router-dom';
import { getProductCategories, getProducts } from '../../services/woocommerce';
import { IconEdit, IconEye, IconTrash } from '../../components/icons/icons';
import Layout from '../../components/Layout';
import DynamicTable from '../../components/Table/DataTable';
import TablePaginationActions from '../../components/Table/tablePagination';

// Define interfaces for TypeScript
interface Category {
  id: number;
  name: string;
}

interface Product {
  id: number;
  name: string;
  price: string;
  stock_quantity: number | null;
  stock_status?: 'instock' | 'outofstock' | 'onbackorder'; // ✅ Added
  categories: { id: number; name: string }[];
  images: { src: string }[];
  store?: { formatted_display_name: string };
}

const ProductList = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  console.log("ProductData:", products);
  const [categories, setCategories] = useState<Category[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [totalCount, setTotalCount] = useState<number>(0);

  // Fetch categories once
  useEffect(() => {
    async function fetchCategories() {
      try {
        const response = await getProductCategories();
        setCategories(response.data);
      } catch {
        setCategories([]);
      }
    }
    fetchCategories();
  }, []);

  // Fetch products on filters or pagination change
  useEffect(() => {
    async function fetchProducts() {
      try {
        setLoading(true);
        setError('');
        const params: any = {
          page: page + 1,
          per_page: rowsPerPage,
        };
        if (searchTerm.trim()) params.search = searchTerm.trim();
        if (selectedCategory) params.category = selectedCategory;
        const response = await getProducts(params);
        setProducts(response.data);
        setTotalCount(Number(response.headers['x-wp-total'] ?? 0));
      } catch (err: any) {
        setError(err.message || 'Failed to fetch products');
      } finally {
        setLoading(false);
      }
    }
    fetchProducts();
  }, [page, rowsPerPage, searchTerm, selectedCategory]);

  const handleSearchTermChange = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
  };

  const handleSelectedCategoryChange = (newCategory: string) => {
    setSelectedCategory(newCategory);
  };

  const clearSearch = () => {
    setSearchTerm('');
  };

  useEffect(() => {
    setPage(0);
  }, [searchTerm, selectedCategory]);

  const renderers = (row: Product, index?: number) => ({
    serial: page * rowsPerPage + (index ?? 0) + 1,
    image:
      row.images && row.images.length > 0 ? (
        <img
          src={row.images[0].src}
          alt={row.name}
          style={{ width: 50, height: 50, objectFit: 'cover', borderRadius: 4 }}
        />
      ) : (
        <Typography variant="caption" color="textSecondary">
          No Image
        </Typography>
      ),
    price: `₹${row.price}`,
    stockQuantity: row.stock_quantity ?? 'N/A',
    categories: row.categories?.map((c) => c.name).join(', ') || '-',
    store: row.store?.formatted_display_name || '-',
  });

  const columns = [
    {
      key: 'serial',
      label: 'S.No',
      align: 'center' as const,
      render: (_row: Product, index: number) => renderers(_row, index).serial,
    },
    { key: 'id', label: 'ID', align: 'center' as const },
    {
      key: 'images',
      label: 'Image',
      align: 'center' as const,
      render: (row: Product) => renderers(row).image,
    },
    { key: 'name', label: 'Name', align: 'left' as const },
    {
      key: 'price',
      label: 'Price',
      align: 'right' as const,
      render: (row: Product) => renderers(row).price,
    },
    {
      key: 'stock_quantity',
      label: 'Stock',
      align: 'center' as const,
      render: (row: Product) => {
        const qty = row.stock_quantity;

        if (qty === null || qty === undefined) {
          return <span style={{ color: '#6b7280' }}>0</span>; 
        }

        if (qty <= 0) {
          return <span style={{ color: '#dc2626', fontWeight: 600 }}>0</span>; 
        }

        return <span style={{ color: '#166534', fontWeight: 600 }}>{qty}</span>;
      },
    },
    {
      key: 'stockstatus',
      label: 'Stock Status',
      align: 'center' as const,
      render: (row: Product) => {
        // 🧾 Debug Log
        console.log("🧾 Stock Status Row:", row?.stock_status);

        // ✅ Determine stock label
        const status =
          row?.stock_status === 'instock'
            ? 'In Stock'
            : row?.stock_status === 'outofstock'
              ? 'Out of Stock'
              : 'Unknown';

        // ✅ Add color indication
        const color =
          row?.stock_status === 'instock'
            ? 'green'
            : row?.stock_status === 'outofstock'
              ? 'red'
              : 'gray';

        return (
          <span
            style={{
              color,
              fontWeight: '600',
              textTransform: 'capitalize',
            }}
          >
            {status}
          </span>
        );
      },
    },
    {
      key: 'categories',
      label: 'Categories',
      align: 'left' as const,
      render: (row: Product) => renderers(row).categories,
    },
    {
      key: 'store',
      label: 'Store',
      align: 'left' as const,
      render: (row: Product) => renderers(row).store,
    },
  ];

  const actions = [
    {
      label: 'View',
      color: '#00bcd4',
      icon: <IconEye size={20} />,
      onClick: (row: Product) => navigate(`/products/view/${row.id}`),
    },
    // {
    //   label: 'Edit',
    //   color: '#4caf50',
    //   icon: <IconEdit size={20} />,
    //   onClick: (row: Product) => navigate(`/editproducts/${row.id}`),
    // },
    // {
    //   label: 'Delete',
    //   color: '#f44336',
    //   icon: <IconTrash size={20} />,
    //   onClick: (row: Product) => alert(`Delete product ${row.id}`),
    // },
  ];

  const handlePageChange = (newPage: number) => setPage(newPage);

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  return (
    <Layout>
      <div className="p-4">
        <div className="flex items-center space-x-2 mb-4">
          <Box sx={{ position: 'relative', flex: 1 }}>
            <TextField
              fullWidth
              label="Search Products"
              value={searchTerm}
              onChange={(e) => handleSearchTermChange(e.target.value)}
              variant="outlined"
              size="small"
            />
            {searchTerm && (
              <IconButton
                onClick={clearSearch}
                sx={{
                  position: 'absolute',
                  right: 8,
                  top: '50%',
                  transform: 'translateY(-50%)',
                }}
              >
                <ClearIcon />
              </IconButton>
            )}
          </Box>
          <Box sx={{ flex: 1 }}>
            <TextField
              select
              fullWidth
              label="Category"
              value={selectedCategory}
              onChange={(e) => handleSelectedCategoryChange(e.target.value)}
              variant="outlined"
              size="small"
            >
              <MenuItem value="">All Categories</MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={String(cat.id)}>
                  {cat.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </div>
        {loading ? (
          <Box display="flex" justifyContent="center" my={5}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Typography color="error" align="center" my={3}>
            {error}
          </Typography>
        ) : (
          <DynamicTable
            columns={columns}
            data={products}
            actions={actions}
            emptyMessage={"No product found"}
            page={page}
            rowsPerPage={rowsPerPage}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            ActionsComponent={TablePaginationActions}
          />
        )}
      </div>
    </Layout>
  );
};

export default ProductList;